import { useEffect, useState, useMemo, useContext } from 'react';
// @mui
import {
  Stack,
  Card,
  Container,
  IconButton,
  Link,
  Typography,
  Button,
  Box, 
  Modal,
  CircularProgress
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import MUIDataTable from "mui-datatables";
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from "@mui/icons-material/Close";
import { TableLoader } from '../../../components/table';
import Iconify from '../../../components/iconify';
import AddDokPegawaiPage from './AddDokPegawaiPage';
import EditDokPegawaiPage from './EditDokPegawaiPage';
import { getCurrentDate } from "../../../utils/formatDate";
// Context
import { axiosPrivate } from '../../../common/axiosPrivate';
import NotificationContext from '../../../context/notificationContext';

export default function DokAllPegawaiPage() {
  const notificationCtx = useContext(NotificationContext);
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [selectedId, setSelectedId] = useState();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState();
  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "_id",
      options: {
        display: false,
        filter: false,
        sort: false
      }
    },
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
      name: "pegawai",
      label: "Pegawai",
      options: {
        filter: false,
        sort: false
      }
    },
    {
     name: "nama",
     label: "Nama Dokumen",
     options: {
      filter: false,
      sort: true,
      customBodyRender: (nama, meta) => {
        const dokumen = meta.rowData[12];
        const fileUrl = dokumen ? `https://drive.google.com/file/d/${dokumen?.id}` : '';
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Iconify icon={'vscode-icons:file-type-pdf2'} width="40px" height="40px" />
              <Typography variant="subtitle2" noWrap>
              <Link href={fileUrl} color="inherit" rel="noopener" target="_blank">
                {nama}
              </Link>
            </Typography>
          </Stack>
        )
      }
     }
    },
    {
      name: "keterangan",
      label: "Keterangan",
      options: {
        filter: false,
        sort: false
      }
    },
    {
      name: "penerima",
      label: "Penerima",
      options: {
        filter: true,
        sort: false,
      }
    },
    {
      name: "penyerah",
      label: "Penyerah",
      options: {
        filter: true,
        sort: false,
      }
    },
    {
      name: "tglTerima",
      label: "Tgl Terima",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "tglSerah",
      label: "Tgl Serah",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "dibuatOleh",
      label: "Dibuat Oleh",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "tglBuat",
      label: "Tgl Buat",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "tglUbah",
      label: "Tgl Ubah",
      options: {
        filter: false,
        sort: false,
        display: false,
        customBodyRender: (value) => value ? format(parseISO(value), 'dd/MM/yyyy') : ''
      }
    },
    {
      name: "dokumen",
      label: "dokumen",
      options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false,
      }
     },
     {
      name: "_id",
      label: "Aksi",
      options: {
       filter: false,
       sort: false,
       customHeadRender: () => null,
       customBodyRender: (value) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleOpenModal(1, value)}
        >
          <Iconify icon={'eva:edit-fill'} />
          Edit
        </Button>
       )
      }
     }
   ];
   
  const options = {
    filterType: "dropdown",
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => deleteLoading ? (
      <CircularProgress
        sx={{
          color: '#637381',
          mr: 2
        }}
        size={20}
      />
    ) : (
      <DeleteIcon
        size={20}
        sx={{
          color: '#637381',
          cursor: 'pointer',
          mr: 2
        }}
        onClick={() => handleDelete(selectedRows, displayData, setSelectedRows)}
      />
    ),
    downloadOptions: {
      filterOptions: {
        filename: `dokumen-${getCurrentDate('-')}.csv`,
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      }
    }
  };

  const handleDelete = (selectedRows, displayData, setSelectedRows) => {
    setDeleteLoading(true);

    const documentIds = selectedRows.data.map(d => displayData[d.index].data[0]);

    axiosPrivate.post('dokumen/destroy/data', {documentIds})
    .then((response) => {
      const count = response?.data?.deletedCount;

      if(count > 0) {
        documentIds.map((item) => setData(prev => prev.filter(e => e._id !== item)));
        setSelectedRows([]);
      }

      if(count > 0) {
        setData(prev => prev.filter(e => e._id !== documentIds))
      }

      handleMessage(response?.data);
    })
    .catch((error) => handleMessage(error?.response))
    .finally(() => setDeleteLoading(false));
  }

  const handleMessage = async (params) => {
    const success = params?.success;

    if(success) {
      notificationCtx.setup({
        severity: 'success',
        title: 'Success',
        message: params?.message
      });
    } else {
      notificationCtx.setup({
        severity: 'error',
        title: 'Error',
        message: params?.message
      });
    }
  };

  const handleOpenModal = (content, id) => {
    setSelectedId(id);
    setModalContent(content);
    setModalOpen(true);
  }

  const handleCloseModal = () => {
    setModalOpen(false);
  }

  const handleUpdateTable = (newData) => {
    const index = data.findIndex(item => item._id === newData._id);
    
    if (index !== -1) {
      data[index] = { ...data[index], ...newData };

      setData(data);
    } else {
      setData(item => [...item, newData]);
    }
  }

  // Fetch Api
  useEffect(() => {
    axiosPrivate.get("dokumen")
    .then((response) => setData(response?.data))
    .catch((error) => console.log(error))
    .finally(() => setTableLoading(false));
  }, []);

  return (
    <>
      <Container
        sx={{
          mt: 5
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="end" sx={{ mb: 2 }}>
          <Button
            onClick={() => handleOpenModal(0)}
            variant='contained'
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            Tambah
          </Button>
        </Stack>
        <Card>
          <MUIDataTable
            title="List Dokumen"
            data={data}
            columns={columns}
            options={options}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>

      <Modal
        sx={{
          overflowY: 'scroll',
          pt: 5,
          pb: 5
        }}
        hideBackdrop
        open={modalOpen}
        onClose={handleCloseModal}
        style={{ backgroundColor: "#fff", backdropFilter: "blur(0px)" }}
      >
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              margin: 1
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseModal}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {modalContent === 0 ? (
            <AddDokPegawaiPage
              handleCloseModal={handleCloseModal}
              handleUpdateTable={handleUpdateTable}
            />
          ) : (
            <EditDokPegawaiPage
              id={selectedId}
              handleCloseModal={handleCloseModal}
              handleUpdateTable={handleUpdateTable}
            />
          )}
        </>
      </Modal>
    </>
  );
}
